import { Box, Flex, Image, Text, useDisclosure } from "@chakra-ui/react"
import { FC } from "react"
import { IoInformationCircleSharp } from "react-icons/io5"
import { useSetRecoilState } from "recoil"
import { AuthPageStyling } from "src/context/types"
import useOnClickWithTouchHandler from "../../../../hooks/useOnClickWithTouchHandler"
import { gridSelectedAvatarAtom } from "../../../../recoil/atoms/avatars"
import {
  AvatarConfig,
  AvatarGesture
} from "../../../../recoil/atoms/avatars/types"
import { checkIsTouchDevice } from "../../../../util"
import { addHitAreaStyle } from "../../../../util/styleUtils"
import ShowCardLoaderOverlay from "../../shows/show-card-loader-overlay"
import { AvatarCardHoverOverlay } from "./avatar-card-hover-overlay"
import AvatarDescriptionModal from "./avatar-description-modal"

type AvatarCardProps = {
  avatarConfig: AvatarConfig
  branding?: AuthPageStyling
  openRoom: (roomHash: string) => void
  loadingRoomHash?: string
  roomHash?: string
  btnText: string
}

export const AvatarCard: FC<AvatarCardProps> = ({
  avatarConfig,
  branding,
  openRoom,
  loadingRoomHash,
  roomHash,
  btnText
}) => {
  const setGridSelectedAvatar = useSetRecoilState(gridSelectedAvatarAtom)
  const listLength: number = 0
  const isCurrentRoomLoading = loadingRoomHash === roomHash
  const isTouchDevice = checkIsTouchDevice()
  const { isOpen, onOpen, onClose } = useDisclosure()

  // don't show the hover overlay if touch device or the other room is joining
  const hideHoverOverlay =
    isTouchDevice || (loadingRoomHash && !isCurrentRoomLoading)

  const handleOnClick = () => {
    setGridSelectedAvatar(avatarConfig)

    // requesting device orientation permission
    !loadingRoomHash && roomHash && openRoom(roomHash)
  }

  const { onTouchEnd, onTouchStart } = useOnClickWithTouchHandler(handleOnClick)

  return (
    <Box
      pos="relative"
      borderRadius="8px"
      overflow="hidden"
      outline="none"
    >
      <Flex
        width="100%"
        flexDir="column"
        padding="8px"
        backgroundColor={branding?.authPagePanelBackgroundColor ?? "gray.200"}
        borderWidth="1px"
        borderColor={branding?.authPagePanelBorderColor ?? "transparent"}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <Flex alignItems="center">
          {/* <UserAvatar
                name={"avatar-name"}
                size={"xs"}
                src={branding.roomAvatarImage}
                colorDisk={"transparent"}
                avatar={null}
                avatarStyle={{ width: "1rem", height: "1rem" }}
                fontSize="0.6rem"
              /> */}
          <Text
            fontSize="0.8rem"
            marginInlineStart="0.3rem"
            fontWeight="500"
            noOfLines={1}
            color={branding?.authPagePanelTextColor ?? "black"}
          >
            {avatarConfig.avatarName}
          </Text>

          {/*  Info icon */}
          <Box
            paddingX={"1"}
            onClick={event => {
              event.stopPropagation()
              onOpen()
            }}
            onTouchEnd={event => event.stopPropagation()}
            position="relative"
            {...addHitAreaStyle({ left: 6, right: 6, top: 8, bottom: 8 })}
          >
            <IoInformationCircleSharp
              color={branding?.authPagePanelTextColor ?? "#1AC541"}
              fontSize="1.2rem"
            />
          </Box>
        </Flex>

        <Flex
          grow={1}
          marginTop="1.5"
          overflow="hidden"
          borderRadius="4px"
          style={{ aspectRatio: "9/16" }}
          position="relative"
          role="group" // added group for applying _groupHover to child
        >
          <Image
            objectFit="cover"
            src={avatarConfig.avatarPhoto}
            alt="Chakra UI"
          />

          {isCurrentRoomLoading && isTouchDevice && <ShowCardLoaderOverlay />}

          <Box
            visibility={isCurrentRoomLoading && !isTouchDevice ? "visible" : "hidden"}
            display="flex"
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            _groupHover={{
              visibility: hideHoverOverlay ? "hidden" : "visible"
            }}
          >
            <AvatarCardHoverOverlay
              branding={branding}
              openShow={handleOnClick}
              btnText={btnText}
              openShowInNewTab={() => {}}
              isRoomloading={!!isCurrentRoomLoading}
              isCurrentRoomLoading={isCurrentRoomLoading}
            />
          </Box>
        </Flex>
      </Flex>

      <AvatarDescriptionModal
        background={branding?.authPagePanelBackgroundColor}
        color={branding?.authPagePanelTextColor}
        isOpen={isOpen}
        onClose={onClose}
        name={avatarConfig.avatarName}
        description={
          avatarConfig.avatarDescription ??
          "This <b>avatar</b> does not currently have a description."
        }
      />
    </Box>
  )
}
